import { Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpResourceSrcPipe } from '@app/pipes/http-resource-src.pipe';
import { ModalController } from '@ionic/angular';

@Component({
  selector: 'app-pdf-viewer',
  templateUrl: './pdf-viewer.component.html',
  styleUrls: ['./pdf-viewer.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    HttpResourceSrcPipe,
  ],
})
export class PdfViewerComponent implements OnInit, OnDestroy {
  @Input('fileId') public fileId: string | null = null;
  public currentPage = 1;
  public totalPages = 1;
  private modalEl?: HTMLIonModalElement;
  public isResizing = false;
  private startX = 0;
  private startY = 0;
  private startWidth = 0;
  private startHeight = 0;
  private hasMoved = false;
  private readonly storageKey = 'pdfModalSize';
  private readonly minWidth = 320;
  private readonly minHeight = 240;
  private readonly maxWidthRatio = 0.98;
  private readonly maxHeightRatio = 0.9;
  private boundMove?: (event: MouseEvent) => void;
  private boundUp?: (event: MouseEvent) => void;
  private desiredWidth = 0;
  private desiredHeight = 0;

  constructor(
    private readonly modalController: ModalController,
    private readonly hostRef: ElementRef<HTMLElement>,
  ) {}

  async ngOnInit() {
    const saved = this.loadSize();
    const defaultWidth = Math.round(window.innerWidth * 0.8);
    const defaultHeight = Math.round(window.innerHeight * 0.75);
    this.resolveModalEl();
    this.applySize(saved?.width ?? defaultWidth, saved?.height ?? defaultHeight);
  }

  ngOnDestroy(): void {
    this.detachResizeListeners();
  }

  public startResize(event: MouseEvent): void {
    event.preventDefault();
    this.isResizing = true;
    this.hasMoved = false;
    this.startX = event.clientX;
    this.startY = event.clientY;
    this.startWidth = this.getCurrentWidth();
    this.startHeight = this.getCurrentHeight();
    this.resolveModalEl();
    const rect = this.modalEl?.getBoundingClientRect();
    if (rect && rect.width > 0 && rect.height > 0) {
      this.startWidth = rect.width;
      this.startHeight = rect.height;
    }
    this.attachResizeListeners();
  }

  private attachResizeListeners(): void {
    this.boundMove = (evt: MouseEvent) => this.onResizeMove(evt);
    this.boundUp = () => this.stopResize();
    document.addEventListener('mousemove', this.boundMove);
    document.addEventListener('mouseup', this.boundUp);
  }

  private detachResizeListeners(): void {
    if (this.boundMove) {
      document.removeEventListener('mousemove', this.boundMove);
    }
    if (this.boundUp) {
      document.removeEventListener('mouseup', this.boundUp);
    }
    this.boundMove = undefined;
    this.boundUp = undefined;
  }

  private onResizeMove(event: MouseEvent): void {
    if (!this.isResizing) {
      return;
    }
    const deltaX = event.clientX - this.startX;
    const deltaY = event.clientY - this.startY;
    if (Math.abs(deltaX) + Math.abs(deltaY) < 2) {
      return;
    }
    this.hasMoved = true;
    const targetWidth = this.startWidth + deltaX;
    const targetHeight = this.startHeight + deltaY;
    this.applySize(targetWidth, targetHeight);
  }

  private stopResize(): void {
    if (!this.isResizing) {
      return;
    }
    this.isResizing = false;
    this.detachResizeListeners();
    if (this.hasMoved) {
      this.saveSize();
    }
  }

  private applySize(width: number, height: number): void {
    this.desiredWidth = width;
    this.desiredHeight = height;
    const maxWidth = Math.floor(window.innerWidth * this.maxWidthRatio);
    const maxHeight = Math.floor(window.innerHeight * this.maxHeightRatio);
    const clampedWidth = Math.min(Math.max(width, this.minWidth), maxWidth);
    const clampedHeight = Math.min(Math.max(height, this.minHeight), maxHeight);
    if (this.modalEl) {
      this.modalEl.style.setProperty('--width', `${clampedWidth}px`);
      this.modalEl.style.setProperty('--height', `${clampedHeight}px`);
      this.modalEl.style.setProperty('--min-width', `${this.minWidth}px`);
      this.modalEl.style.setProperty('--min-height', `${this.minHeight}px`);
    }
  }

  private getCurrentWidth(): number {
    const current = this.modalEl?.style.getPropertyValue('--width') || '';
    const parsed = parseInt(current || '0', 10);
    return Number.isNaN(parsed) ? Math.round(window.innerWidth * 0.8) : parsed;
  }

  private getCurrentHeight(): number {
    const current = this.modalEl?.style.getPropertyValue('--height') || '';
    const parsed = parseInt(current || '0', 10);
    return Number.isNaN(parsed) ? Math.round(window.innerHeight * 0.75) : parsed;
  }

  private saveSize(): void {
    const payload = {
      width: this.getCurrentWidth(),
      height: this.getCurrentHeight(),
    };
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(payload));
    } catch {
      // Ignore storage errors
    }
  }

  private loadSize(): { width: number; height: number } | null {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) {
        return null;
      }
      const parsed = JSON.parse(raw) as { width?: number; height?: number };
      if (!parsed.width || !parsed.height) {
        return null;
      }
      return { width: parsed.width, height: parsed.height };
    } catch {
      return null;
    }
  }

  private resolveModalEl(): void {
    if (this.modalEl) {
      return;
    }
    const host = this.hostRef.nativeElement;
    const modal = host.closest('ion-modal') as HTMLIonModalElement | null;
    if (modal) {
      this.modalEl = modal;
      if (this.desiredWidth && this.desiredHeight) {
        this.applySize(this.desiredWidth, this.desiredHeight);
      }
      return;
    }
    this.modalController.getTop().then((top) => {
      this.modalEl = top || undefined;
      if (this.modalEl && this.desiredWidth && this.desiredHeight) {
        this.applySize(this.desiredWidth, this.desiredHeight);
      }
    });
  }
}